import { Button as KButton } from '@kobalte/core/button'
import { Select } from '@kobalte/core/select'
import { Slider } from '@kobalte/core/slider'
import { Switch } from '@kobalte/core/switch'
import { ToggleGroup } from '@kobalte/core/toggle-group'
import { For, type JSX } from 'solid-js'
import { round } from './util'

export function Section(props: { title: string; children: JSX.Element; aside?: JSX.Element }) {
  return (
    <section class="section">
      <header class="section__head">
        <h3>{props.title}</h3>
        {props.aside}
      </header>
      <div class="section__body">{props.children}</div>
    </section>
  )
}

export function Row(props: { label: string; children: JSX.Element; hint?: string }) {
  return (
    <div class="row" title={props.hint}>
      <span class="row__label">{props.label}</span>
      <div class="row__control">{props.children}</div>
    </div>
  )
}

export function Btn(props: {
  children: JSX.Element
  onClick?: () => void
  title?: string
  active?: boolean
  danger?: boolean
  accent?: boolean
  disabled?: boolean
  small?: boolean
  testid?: string
  class?: string
}) {
  return (
    <KButton
      class={`btn ${props.active ? 'is-active' : ''} ${props.danger ? 'btn--danger' : ''} ${props.accent ? 'btn--accent' : ''} ${props.small ? 'btn--small' : ''} ${props.class ?? ''}`}
      onClick={() => props.onClick?.()}
      title={props.title}
      disabled={props.disabled}
      data-testid={props.testid}
    >
      {props.children}
    </KButton>
  )
}

/** Numeric input that commits on Enter, blur, or the spinner — never on every keystroke. */
export function NumberInput(props: {
  value: number
  /** Return false to refuse the value; the field then snaps back to the current one. */
  onCommit: (v: number) => unknown
  step?: number
  min?: number
  max?: number
  places?: number
  label: string
  testid?: string
}) {
  return (
    <input
      class="num"
      type="number"
      aria-label={props.label}
      data-testid={props.testid}
      step={props.step ?? 0.1}
      min={props.min}
      max={props.max}
      value={round(props.value, props.places ?? 2)}
      onChange={(e) => {
        let v = e.currentTarget.valueAsNumber
        if (!Number.isFinite(v)) {
          e.currentTarget.value = String(round(props.value, props.places ?? 2))
          return
        }
        if (props.min !== undefined) v = Math.max(props.min, v)
        if (props.max !== undefined) v = Math.min(props.max, v)
        if (props.onCommit(v) === false) {
          e.currentTarget.value = String(round(props.value, props.places ?? 2))
          return
        }
        e.currentTarget.value = String(round(v, props.places ?? 2))
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
      }}
    />
  )
}

export function Vec3Input(props: {
  label: string
  value: readonly [number, number, number]
  onCommit: (v: [number, number, number]) => void
  step?: number
  min?: number
  max?: number
  axes?: [string, string, string]
  places?: number
  testid?: string
}) {
  const axes = () => props.axes ?? ['X', 'Y', 'Z']
  return (
    <div class="row row--stack">
      <span class="row__label">{props.label}</span>
      <div class="vec3">
        <For each={[0, 1, 2] as const}>
          {(i) => (
            <div class="vec3__cell">
              <span>{axes()[i]}</span>
              <NumberInput
                label={`${props.label} ${axes()[i]}`}
                testid={props.testid ? `${props.testid}-${i}` : undefined}
                value={props.value[i]}
                step={props.step}
                min={props.min}
                max={props.max}
                places={props.places}
                onCommit={(v) => {
                  const next: [number, number, number] = [props.value[0], props.value[1], props.value[2]]
                  next[i] = v
                  props.onCommit(next)
                }}
              />
            </div>
          )}
        </For>
      </div>
    </div>
  )
}

export function SliderRow(props: {
  label: string
  value: number
  min?: number
  max?: number
  step?: number
  onInput: (v: number) => void
  format?: (v: number) => string
  hint?: string
  testid?: string
}) {
  const fmt = () => (props.format ? props.format(props.value) : String(round(props.value, 2)))
  return (
    <Slider
      class="slider"
      value={[props.value]}
      minValue={props.min ?? 0}
      maxValue={props.max ?? 1}
      step={props.step ?? 0.01}
      onChange={(v) => props.onInput(v[0]!)}
      getValueLabel={() => fmt()}
      title={props.hint}
      data-testid={props.testid}
    >
      <div class="slider__head">
        <Slider.Label class="row__label">{props.label}</Slider.Label>
        <Slider.ValueLabel class="slider__value" />
      </div>
      <Slider.Track class="slider__track">
        <Slider.Fill class="slider__fill" />
        <Slider.Thumb class="slider__thumb">
          <Slider.Input />
        </Slider.Thumb>
      </Slider.Track>
    </Slider>
  )
}

export function SwitchRow(props: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string; testid?: string }) {
  return (
    <Switch class="switch" checked={props.checked} onChange={props.onChange} title={props.hint} data-testid={props.testid}>
      <Switch.Input />
      <Switch.Control class="switch__control">
        <Switch.Thumb class="switch__thumb" />
      </Switch.Control>
      <Switch.Label class="switch__label">{props.label}</Switch.Label>
    </Switch>
  )
}

export interface Option<T extends string> {
  value: T
  label: string
}

export function SelectRow<T extends string>(props: {
  label: string
  value: T
  options: Option<T>[]
  onChange: (v: T) => void
  testid?: string
}) {
  const current = () => props.options.find((o) => o.value === props.value) ?? props.options[0]!
  return (
    <Row label={props.label}>
      <Select<Option<T>>
        options={props.options}
        optionValue="value"
        optionTextValue="label"
        value={current()}
        onChange={(o) => o && props.onChange(o.value)}
        disallowEmptySelection
        itemComponent={(p) => (
          <Select.Item item={p.item} class="select__item">
            <Select.ItemLabel>{p.item.rawValue.label}</Select.ItemLabel>
            <Select.ItemIndicator class="select__tick">●</Select.ItemIndicator>
          </Select.Item>
        )}
      >
        <Select.Trigger class="select__trigger" aria-label={props.label} data-testid={props.testid}>
          <Select.Value<Option<T>>>{(s) => s.selectedOption().label}</Select.Value>
          <Select.Icon class="select__icon">▾</Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content class="select__content">
            <Select.Listbox class="select__list" />
          </Select.Content>
        </Select.Portal>
      </Select>
    </Row>
  )
}

export function Segmented<T extends string>(props: {
  value: T
  options: { value: T; label: JSX.Element; title?: string }[]
  onChange: (v: T) => void
  label: string
  testid?: string
}) {
  return (
    <ToggleGroup
      class="segmented"
      value={props.value}
      onChange={(v) => v && props.onChange(v as T)}
      aria-label={props.label}
      data-testid={props.testid}
    >
      <For each={props.options}>
        {(o) => (
          <ToggleGroup.Item
            class="segmented__item"
            value={o.value}
            title={o.title}
            data-testid={props.testid ? `${props.testid}-${o.value}` : undefined}
          >
            {o.label}
          </ToggleGroup.Item>
        )}
      </For>
    </ToggleGroup>
  )
}

export function Meter(props: { level: number }) {
  // RMS → rough dB scale, -60 … 0
  const pct = () => {
    const db = 20 * Math.log10(Math.max(props.level, 1e-5))
    return Math.max(0, Math.min(100, ((db + 60) / 60) * 100))
  }
  return (
    // biome-ignore lint/a11y/useSemanticElements: <meter> cannot be styled consistently across Chrome and Firefox
    <div class="meter" role="meter" aria-label="Output level" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct())}>
      <div class="meter__fill" style={{ width: `${pct()}%` }} />
    </div>
  )
}
